import {Suspense, useEffect, useMemo} from "react";
import {BrowserRouter as Router, Route, Routes} from "react-router-dom";
import {useSelector, useDispatch, shallowEqual} from "react-redux";

import ProtectedRoute from "./ProtectedRoute";
import PrivateRoute from "./PrivateRoute";

import {loadAppSettings, loadAllFormQuestions} from "@/features/appSettings";
import {logout} from "@/features/auth";
import {useSync} from "@/hooks/useSync/useSync.js";

import ErrorBoundary from "@/components/Global/ErrorBoundary/ErrorBoundary";
import Loader from "@/components/Global/Loader/Loader";
import MaintenancePage from "@/components/Global/MaintenancePage/MaintenancePage";
import PageSanitiser from "@/components/Global/PageSanitiser/PageSanitiser";
import UpdateCachePrompt from "@/components/Global/UpdateCachePrompt/UpdateCachePrompt";

import {
  ActivateKit,
  ActivateKitNav,
  ActiveKit,
  BespokeTest,
  BookPhlebotomyAppointment,
  ChangePassword,
  Clinic,
  ClinicFinderContainer,
  Credit,
  Dashboard,
  DeleteAccount,
  FemaleQuestions,
  HealthProfile,
  HealthScore,
  HealthScoreDetails,
  Help,
  Learn,
  LoadingData,
  ManageHealthCategories,
  ManagePin,
  OrderHistory,
  Page404,
  Plans,
  Post,
  PrivacyPolicy,
  RateApp,
  Redirect,
  ReferAFriend,
  ResetPassword,
  Results,
  Shop,
  SignIn,
  Signup,
  Subscriptions,
  TermsAndConditions,
  ThirdPartyAccess,
  WhatsNew,
  YourResult,
} from "@/containers";

import Layout from "@/components/Layout/Layout";
import NavigationMenu from "@/components/Global/Menus/Navigation/NavigationMenu";
import Notifications from "@/components/Notifications/Notifications";

import {checkIsDemoAccount} from "@/helpers/checkIsDemoAccount";
import {AppVisibilityProvider} from "@/contexts/AppVisibilityContext";
import {TrackingProvider} from "@/contexts/TrackingContext";
import {useWhiteLabelTheme} from "@/hooks/useWhiteLabelTheme/useWhiteLabelTheme";

import {config} from "@/config";
import {selectAllSettingsData, selectIsOnline} from "@/features/appSettings/appSettingsSelectors";
import {selectIsAuthenticated, selectIsLoggingOut, selectToken} from "@/features/auth/authSelectors";
import {selectUserDetails} from "@/features/user/userSelectors";
import {selectPartnersShowHcReports} from "@/features/partners/partnersSelectors";

const {websiteId} = config;
const isForth = websiteId === 1;
const isEdge = websiteId === 2;
const isBiomarkerTracking = !isForth && !isEdge;
const isWhiteLabelApp = window.config.isWhiteLabelApp;

const SHOP_BASE_URL = config.shopHostname;

export default function ForthRoute() {
  const queryParams = new URLSearchParams(window.location.search);
  const hasForgotPassword = queryParams.has("forgot-password");

  const dispatch = useDispatch();
  const {showLoadingScreen, loadingScreenMsg} = useSync();

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoggingOut = useSelector(selectIsLoggingOut);
  const globalStateToken = useSelector(selectToken);
  const isAppSettingStateEmpty = Object.values(useSelector(selectAllSettingsData, shallowEqual)).length === 0;
  const userId = useSelector(selectUserDetails, shallowEqual)?.ID || 0;
  const showB2BHcReports = useSelector(selectPartnersShowHcReports);
  const isOnline = useSelector(selectIsOnline);
  const {themeConfig} = useWhiteLabelTheme();
  const showThirdPartyAccess = themeConfig?.settings?.showThirdPartyAccess ?? true;

  useEffect(() => {
    if (isAuthenticated && hasForgotPassword) {
      dispatch(logout()).then(() => {
        localStorage.setItem("hasForgottenPassword", true);
      });
    }

    const checkLocalAuth = async () => {
      if (globalStateToken && !localStorage.getItem("token")) {
        localStorage.setItem("token", globalStateToken);
      }
      return Promise.resolve(globalStateToken);
    };

    checkLocalAuth().then(response => {
      if (isOnline) {
        if (isAppSettingStateEmpty) {
          dispatch(loadAppSettings());
        }

        if (isAuthenticated && !isAppSettingStateEmpty && !window.location.pathname.includes("bespoke-test")) {
          dispatch(loadAllFormQuestions());
        }

        if (!isAuthenticated) {
          localStorage.removeItem("dataLoaded");
          localStorage.removeItem("lastSync");
        }
      }
    });
  }, [isOnline, dispatch, isAuthenticated]);

  const isDemoAccount = useMemo(() => checkIsDemoAccount(userId), [userId]);
  const hasPartnerToken = queryParams.has("pt");
  const partnerPlatformUrl = window.config?.isDev
    ? "https://appdev.forthpartners.co.uk"
    : "https://forthpartners.co.uk";

  return (
    <>
      <Suspense fallback={<Loader variant="fullWindow" />}>
        <Router>
          <TrackingProvider>
            <ErrorBoundary fallback={<MaintenancePage />}>
              <AppVisibilityProvider>
                <PageSanitiser>
                  <Routes>
                    {/* <Route
                path="/bespoke-test/:parentTestId/:testType?/:preData?/:chatTest?/:voucherCode?/:partnerId?/:checkoutFromPartnerId?"
                element={<Redirect to="https://shop.forthwithlife.co.uk/bespoke-test/" variant="bespoke-builder" />}
              /> */}
                    <Route
                      path="return-trustpilot/:orderId"
                      element={
                        <Redirect to="https://www.forthwithlife.co.uk/trustpilot-thank-you" variant="trustpilot" />
                      }
                    />
                    <Route
                      path="return-trustpilot-product/:orderId"
                      element={
                        <Redirect
                          to="https://www.forthwithlife.co.uk/trustpilot-thank-you"
                          variant="trustpilotproduct"
                        />
                      }
                    />
                    <Route
                      path="partner-bespoke-test/*"
                      element={<ProtectedRoute isAllow={hasPartnerToken} redirectPath={partnerPlatformUrl} />}>
                      <Route path=":partnerId" element={<BespokeTest title="Bespoke test" />} />
                      <Route path=":partnerId/:parentTestId" element={<BespokeTest title="Bespoke test" />} />
                      <Route path="*" element={<Page404 />} />
                    </Route>
                    <Route path="create-partner-test/*" element={<BespokeTest title="Bespoke test" />}>
                      <Route path=":partnerRequestCode" element={<BespokeTest title="Bespoke test" />} />
                      <Route path=":partnerRequestCode/:parentTestId" element={<BespokeTest title="Bespoke test" />} />
                      <Route path="*" element={<Page404 />} />
                    </Route>
                    <Route path="/bespoke-test/*" element={<BespokeTest title="Bespoke test" />}>
                      <Route path=":parentTestId" element={<BespokeTest title="Bespoke test" />} />
                      <Route path=":parentTestId/:phlebotomyOption" element={<BespokeTest title="Bespoke test" />} />
                      <Route
                        path=":parentTestId/:phlebotomyOption/:testAdded"
                        element={<BespokeTest title="Bespoke test" />}
                      />
                      <Route
                        path=":parentTestId/:phlebotomyOption/:testAdded/:chatTest/:voucherCode"
                        element={<BespokeTest title="Bespoke test" />}
                      />
                      <Route
                        path=":parentTestId/:phlebotomyOption/:testAdded/:chatTest/:voucherCode/:partnerId"
                        element={<BespokeTest title="Bespoke test" />}
                      />
                    </Route>
                    <Route path="/edit-test/*" element={<BespokeTest title="Bespoke test" />}>
                      <Route path=":editTestId" element={<BespokeTest title="Bespoke test" />} />
                      <Route path=":editTestId/:phlebotomyOption" element={<BespokeTest title="Bespoke test" />} />
                      <Route
                        path=":editTestId/:phlebotomyOption/:voucherCode"
                        element={<BespokeTest title="Bespoke test" />}
                      />
                    </Route>
                    <Route
                      path="/book-phlebotomy-appointment/:bookingToken"
                      element={<BookPhlebotomyAppointment title="Book phlebotomy appointment" />}
                    />
                    <Route path="/clinic-finder" element={<ClinicFinderContainer title="Clinic Finder" />} />
                    <Route path="/*" element={<ProtectedRoute isAllow={!isAuthenticated} />}>
                      <Route path="forgotten-password/:code?" element={<ResetPassword title="Forgotten Password" />} />
                      <Route path="invite/:code?" element={<ResetPassword title="Invite" />} />
                      <Route path="sign-in" element={<SignIn title="SignIn" />} />
                      <Route path="sign-in/:code" element={<SignIn title="SignIn" />} />
                      <Route path="demo" element={<SignIn title="SignIn" />} />
                      <Route path="fhmdemo" element={<SignIn title="SignIn" />} />
                      <Route path="activate" element={<ActivateKit title="ActivateKit" />} />
                      <Route path="create" element={<Signup title="Signup" />} />
                      {/* <Route path="magicLink" element={<MagicLink title="MagicLink" />} /> */}
                    </Route>
                    <Route element={<Layout />}>
                      <Route path="/*" element={<PrivateRoute isAuthenticated={isAuthenticated} />}>
                        <Route exact index element={<Dashboard title="Dashboard" />} />
                        <Route exact path="active-kit/:orderId" element={<ActiveKit title="Active Kit" />} />
                        <Route exact path="results" element={<Results title="Results" />} />
                        <Route exact path="results/:orderId" element={<YourResult title="Results" />} />
                        <Route
                          element={
                            <ProtectedRoute isAllow={websiteId === 1 || (websiteId === 3 && showB2BHcReports)} />
                          }>
                          <Route
                            exact
                            path="health-score/:orderId/:healthCategoryId"
                            element={<HealthScore title="Health Score" />}
                          />
                          <Route
                            exact
                            path="health-score/:orderId/:healthCategoryId/:healthComponentId"
                            element={<HealthScoreDetails title="Health Score" />}
                          />
                        </Route>
                        <Route element={<ProtectedRoute isAllow={websiteId === 1} />}>
                          <Route exact path="plans" element={<Plans title="Health Plans" />} />
                          <Route exact path="clinic" element={<Clinic title="Clinic" />} />
                        </Route>
                        <Route exact path="shop" element={<Shop title="Shop" />} />
                        {/* <Route exact path="explore" element={<Explore title="Explore" />} /> */}
                        <Route
                          element={
                            <ProtectedRoute isAllow={[1, 2].some(websiteId => websiteId === config.websiteId)} />
                          }>
                          <Route exact path="learn" element={<Learn title="Learn" />} />
                          <Route exact path="learn/:slug" element={<Post title="Learn" />} />
                        </Route>
                        {/* isDemoAccount */}
                        <Route path="/*" element={<PrivateRoute isAuthenticated={!isDemoAccount} />}>
                          <Route exact path="get-started" element={<FemaleQuestions title="Female Questions" />}>
                            <Route exact path=":orderId" element={<FemaleQuestions title="Female Questions" />} />
                          </Route>
                          <Route path="my-account">
                            <Route exact path="health-profile" element={<HealthProfile title="Health Profile" />} />
                            <Route path="account-settings">
                              <Route
                                exact
                                path="change-password"
                                element={<ChangePassword title="Account Settings - Change Password" />}
                              />
                              <Route exact path="manage-pin" element={<ManagePin title="Manage pin" />} />
                              <Route element={<ProtectedRoute isAllow={websiteId === 1} />}>
                                <Route
                                  exact
                                  path="manage-health-categories"
                                  element={<ManageHealthCategories title="Manage health categories" />}
                                />
                              </Route>

                              <Route
                                exact
                                path="notification-settings"
                                element={"Account Settings - Notification Settings"}
                              />
                              <Route exact path="insight-settings" element={"Account Settings - Insight Settings"} />
                              <Route exact path="doctors-review" element={"Account Settings - Doctor's Review"} />
                              <Route
                                path="*"
                                element={
                                  <PrivateRoute isAuthenticated={isWhiteLabelApp ? showThirdPartyAccess : true} />
                                }>
                                <Route
                                  exact
                                  path="third-party"
                                  element={<ThirdPartyAccess title="Third Party Access" />}
                                />
                              </Route>
                              <Route exact path="beta-features" element={"Account Settings - Beta Features"} />
                              <Route index element={<NavigationMenu variant="MyAccount" />} />
                            </Route>
                            <Route exact path="subscriptions" element={<Subscriptions title="Subscriptions" />} />
                            <Route exact path="order-history" element={<OrderHistory title="Order History" />}>
                              <Route exact path=":orderId/:page" element={<OrderHistory title="Order History" />} />
                            </Route>
                            <Route element={<ProtectedRoute isAllow={websiteId == 1} />}>
                              <Route exact path="consultations" element={"My Consultations"} />
                            </Route>
                            <Route exact path="credit" element={<Credit title="Credit" />} />
                            <Route exact path="activate-kit" element={<ActivateKitNav title="Activate Kit" />} />
                            <Route exact path="upgrade" element={"Upgrade to pro"} />
                            <Route exact path="invite" element={<ReferAFriend title="Invite Friends & Family" />} />
                            <Route exact path="whats-new" element={<WhatsNew title="What's New?" />} />
                            <Route
                              element={
                                <ProtectedRoute
                                  isAllow={
                                    [1, 2, 3].some(websiteId => websiteId === config.websiteId) &&
                                    !config.isWhiteLabelApp
                                  }
                                />
                              }>
                              <Route exact path="help" element={<Help title="Help" />} />
                            </Route>{" "}
                            <Route exact path="rate-app" element={<RateApp title="Rate the app" />} />
                            <Route exact path="employee-programme" element={"Employee Programme"} />
                            <Route index element={<NavigationMenu variant="MyAccount" />} />
                          </Route>
                          <Route path="tcs-and-privacy" element={<NavigationMenu variant="MyAccount" />} />
                          <Route
                            exact
                            path="terms-conditions"
                            element={<TermsAndConditions title="Terms & Conditions" />}
                          />
                          <Route exact path="privacy-policy" element={<PrivacyPolicy title="Privacy Policy" />} />
                          <Route exact path="delete" element={<DeleteAccount title="Delete my account" />} />
                        </Route>
                        <Route path="*" element={<Page404 />} />
                      </Route>
                    </Route>
                  </Routes>
                </PageSanitiser>
              </AppVisibilityProvider>
            </ErrorBoundary>
          </TrackingProvider>

          <LoadingData
            show={showLoadingScreen}
            loadingScreenMsg={loadingScreenMsg}
            variant={
              websiteId == 1
                ? "forth"
                : websiteId == 2
                  ? "edge"
                  : websiteId == 3 && window.config.isWhiteLabelApp
                    ? "white-label"
                    : "forth"
            }
          />
          <Notifications />
          <UpdateCachePrompt isLoading={showLoadingScreen} />
        </Router>
      </Suspense>
    </>
  );
}
